import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import {
  FaArrowDown,
  FaArrowUp,
  FaBalanceScale,
  FaCarSide,
  FaChartLine,
  FaCoins,
  FaExclamationTriangle,
  FaFilter,
  FaInfoCircle,
  FaMoneyBillWave,
  FaSearch,
  FaSortAmountDown,
  FaTimes,
  FaTrophy,
} from 'react-icons/fa';
import './RentabilidadeFrota.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value || 0));

const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;

const toNumber = (value) => Number(value || 0);

const getPlateDisplay = (plate = '') => {
  const clean = String(plate).replace(/\s/g, '').toUpperCase();
  return clean || '---0000';
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getVehiclePerformanceScore = (vehicle) => {
  const receita = toNumber(vehicle.total_receita);
  const despesa = toNumber(vehicle.total_despesa);
  const lucro = toNumber(vehicle.lucro_liquido);
  const roi = toNumber(vehicle.roi);
  const margem = receita > 0 ? (lucro / receita) * 100 : 0;
  const custoSobreReceita = receita > 0 ? (despesa / receita) * 100 : receita === 0 && despesa > 0 ? 100 : 0;

  let score = 50;

  score += margem * 0.8;
  score += roi * 0.35;
  score -= custoSobreReceita * 0.25;

  if (receita === 0 && despesa === 0) score = 35;
  if (receita === 0 && despesa > 0) score -= 25;
  if (lucro < 0) score -= 15;
  if (margem > 25) score += 10;
  if (roi > 20) score += 8;

  return Math.round(clamp(score, 0, 100));
};

const getScoreTone = (score) => {
  if (score >= 75) return 'elite';
  if (score >= 55) return 'good';
  if (score >= 35) return 'attention';
  return 'critical';
};

const getVehicleInsights = (vehicle) => {
  const receita = toNumber(vehicle.total_receita);
  const despesa = toNumber(vehicle.total_despesa);
  const lucro = toNumber(vehicle.lucro_liquido);
  const roi = toNumber(vehicle.roi);
  const margem = receita > 0 ? (lucro / receita) * 100 : 0;
  const custoSobreReceita = receita > 0 ? (despesa / receita) * 100 : receita === 0 && despesa > 0 ? 100 : 0;

  const insights = [];

  if (receita === 0 && despesa === 0) {
    insights.push({
      type: 'neutral',
      title: 'Ativo sem movimentação',
      text: 'Este veículo não registrou receita nem despesa no período filtrado. Vale validar se houve ociosidade operacional ou ausência de apontamentos.',
    });
  }

  if (receita === 0 && despesa > 0) {
    insights.push({
      type: 'critical',
      title: 'Custo sem faturamento',
      text: 'Houve despesa no período sem geração de receita. Este cenário corrói margem e merece revisão imediata da operação vinculada ao veículo.',
    });
  }

  if (lucro < 0) {
    insights.push({
      type: 'critical',
      title: 'Operação deficitária',
      text: 'O veículo encerrou o período no prejuízo. Recomenda-se revisar custo operacional, consumo, manutenção e aderência do preço praticado.',
    });
  } else if (margem >= 20) {
    insights.push({
      type: 'positive',
      title: 'Boa geração de valor',
      text: 'A margem está em patamar saudável, indicando boa eficiência entre faturamento e custo operacional.',
    });
  } else if (margem > 0) {
    insights.push({
      type: 'attention',
      title: 'Rentável, porém pressionado',
      text: 'O veículo ainda é rentável, mas com margem mais apertada. Uma pequena alta de custo pode reduzir a competitividade da operação.',
    });
  }

  if (custoSobreReceita >= 85) {
    insights.push({
      type: 'attention',
      title: 'Custo muito comprimido contra a receita',
      text: 'As despesas consomem parcela muito alta da receita. É recomendado acompanhar combustível, corretivas, pneus e tempo improdutivo.',
    });
  }

  if (roi >= 20) {
    insights.push({
      type: 'positive',
      title: 'ROI destacado',
      text: 'O retorno sobre o investimento está forte neste período, sugerindo boa utilização do ativo.',
    });
  } else if (roi > 0 && roi < 8) {
    insights.push({
      type: 'attention',
      title: 'ROI abaixo do potencial',
      text: 'O retorno existe, mas está abaixo do ideal. Vale comparar com veículos equivalentes ou com a rota/uso atual.',
    });
  }

  if (insights.length === 0) {
    insights.push({
      type: 'neutral',
      title: 'Desempenho estável',
      text: 'Os indicadores estão em zona neutra. O próximo passo é observar tendência de margem e custo ao longo dos próximos períodos.',
    });
  }

  return insights.slice(0, 4);
};

const getExecutiveSummary = (vehicle) => {
  const receita = toNumber(vehicle.total_receita);
  const despesa = toNumber(vehicle.total_despesa);
  const lucro = toNumber(vehicle.lucro_liquido);
  const margem = receita > 0 ? (lucro / receita) * 100 : 0;
  const roi = toNumber(vehicle.roi);
  const score = getVehiclePerformanceScore(vehicle);

  if (receita === 0 && despesa === 0) {
    return 'Veículo sem movimentação financeira no período analisado. O ativo aparenta estar ocioso ou sem apontamento operacional suficiente.';
  }

  if (lucro < 0) {
    return `O veículo operou em prejuízo no período, com margem de ${formatPercent(margem)}. O cenário pede revisão prioritária de custos, produtividade e alocação do ativo.`;
  }

  if (score >= 75) {
    return `Veículo com performance de destaque, sustentando lucro consistente, margem saudável (${formatPercent(
      margem
    )}) e boa eficiência operacional. É um candidato natural a benchmark interno.`;
  }

  if (roi >= 15 && margem >= 15) {
    return `O veículo demonstra equilíbrio relevante entre geração de caixa e custo operacional, com ROI de ${formatPercent(
      roi
    )} e margem positiva.`;
  }

  return `O veículo permanece rentável, mas ainda há espaço para refinamento operacional, especialmente no controle de custo e na ampliação da margem líquida.`;
};

export default function RentabilidadeFrota() {
  const [dadosFrota, setDadosFrota] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('mensal');

  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [ordenacao, setOrdenacao] = useState('lucro_desc');
  const [quickFilter, setQuickFilter] = useState('todos');
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/api/rentabilidade/frota?periodo=${periodo}`);
        setDadosFrota(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
        setDadosFrota([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [periodo]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') setSelectedVehicle(null);
    };

    if (selectedVehicle) {
      window.addEventListener('keydown', handleEsc);
    }

    return () => window.removeEventListener('keydown', handleEsc);
  }, [selectedVehicle]);

  const frotaEnriquecida = useMemo(() => {
    return dadosFrota.map((v, index) => {
      const totalReceita = toNumber(v.total_receita);
      const totalDespesa = toNumber(v.total_despesa);
      const lucroLiquido = toNumber(v.lucro_liquido);
      const roi = toNumber(v.roi);
      const margem = totalReceita > 0 ? (lucroLiquido / totalReceita) * 100 : 0;
      const custoSobreReceita =
        totalReceita > 0
          ? (totalDespesa / totalReceita) * 100
          : totalReceita === 0 && totalDespesa > 0
          ? 100
          : 0;

      const performanceScore = getVehiclePerformanceScore(v);
      const statusKey = lucroLiquido >= 0 ? 'profitable' : 'loss';
      const statusLabel = lucroLiquido >= 0 ? 'Rentável' : 'Prejuízo';
      const plateLabel = getPlateDisplay(v.placa);
      const insights = getVehicleInsights(v);
      const executiveSummary = getExecutiveSummary(v);

      return {
        ...v,
        id: v.id || `${plateLabel}-${index}`,
        total_receita: totalReceita,
        total_despesa: totalDespesa,
        lucro_liquido: lucroLiquido,
        roi,
        margem,
        custoSobreReceita,
        performanceScore,
        scoreTone: getScoreTone(performanceScore),
        statusKey,
        statusLabel,
        plateLabel,
        insights,
        executiveSummary,
      };
    });
  }, [dadosFrota]);

  const analiseGeral = useMemo(() => {
    const totalReceita = frotaEnriquecida.reduce((acc, v) => acc + v.total_receita, 0);
    const totalDespesa = frotaEnriquecida.reduce((acc, v) => acc + v.total_despesa, 0);
    const lucroTotal = totalReceita - totalDespesa;
    const margemLucro = totalReceita > 0 ? (lucroTotal / totalReceita) * 100 : 0;

    const sortedByLucro = [...frotaEnriquecida].sort((a, b) => b.lucro_liquido - a.lucro_liquido);
    const melhorVeiculo = sortedByLucro[0] || null;
    const piorVeiculo = sortedByLucro[sortedByLucro.length - 1] || null;
    const veiculosPrejuizo = frotaEnriquecida.filter((v) => v.lucro_liquido < 0).length;
    const ociosos = frotaEnriquecida.filter((v) => v.total_receita === 0 && v.total_despesa === 0).length;
    const scoreMedio =
      frotaEnriquecida.length > 0
        ? frotaEnriquecida.reduce((acc, v) => acc + v.performanceScore, 0) / frotaEnriquecida.length
        : 0;

    return {
      totalReceita,
      totalDespesa,
      lucroTotal,
      margemLucro,
      melhorVeiculo,
      piorVeiculo,
      veiculosPrejuizo,
      ociosos,
      scoreMedio,
    };
  }, [frotaEnriquecida]);

  const frotaFiltrada = useMemo(() => {
    let lista = [...frotaEnriquecida];

    if (busca.trim()) {
      const termo = busca.trim().toLowerCase();
      lista = lista.filter(
        (v) =>
          String(v.modelo || '').toLowerCase().includes(termo) ||
          String(v.placa || '').toLowerCase().includes(termo)
      );
    }

    if (filtroStatus === 'rentavel') {
      lista = lista.filter((v) => v.lucro_liquido >= 0);
    } else if (filtroStatus === 'prejuizo') {
      lista = lista.filter((v) => v.lucro_liquido < 0);
    }

    if (quickFilter === 'prejuizo') {
      lista = lista.filter((v) => v.lucro_liquido < 0);
    } else if (quickFilter === 'top_margem') {
      lista = lista.filter((v) => v.margem >= 20);
    } else if (quickFilter === 'ociosos') {
      lista = lista.filter((v) => v.total_receita === 0 && v.total_despesa === 0);
    }

    switch (ordenacao) {
      case 'lucro_asc':
        lista.sort((a, b) => a.lucro_liquido - b.lucro_liquido);
        break;
      case 'margem_desc':
        lista.sort((a, b) => b.margem - a.margem);
        break;
      case 'receita_desc':
        lista.sort((a, b) => b.total_receita - a.total_receita);
        break;
      case 'roi_desc':
        lista.sort((a, b) => b.roi - a.roi);
        break;
      case 'modelo_asc':
        lista.sort((a, b) => String(a.modelo || '').localeCompare(String(b.modelo || '')));
        break;
      case 'lucro_desc':
      default:
        lista.sort((a, b) => b.lucro_liquido - a.lucro_liquido);
        break;
    }

    return lista;
  }, [frotaEnriquecida, busca, filtroStatus, ordenacao, quickFilter]);

  const dadosGrafico = useMemo(() => {
    const base = [...frotaFiltrada]
      .sort((a, b) => b.total_receita - a.total_receita)
      .slice(0, 12);

    return {
      labels: base.map((v) => v.plateLabel),
      datasets: [
        {
          type: 'bar',
          label: 'Receita',
          backgroundColor: 'rgba(17, 36, 124, 0.88)',
          borderRadius: 10,
          maxBarThickness: 34,
          data: base.map((v) => v.total_receita),
          yAxisID: 'y',
          order: 2,
        },
        {
          type: 'bar',
          label: 'Despesa',
          backgroundColor: 'rgba(220, 53, 69, 0.72)',
          borderRadius: 10,
          maxBarThickness: 34,
          data: base.map((v) => v.total_despesa),
          yAxisID: 'y',
          order: 3,
        },
        {
          type: 'line',
          label: 'Margem %',
          borderColor: '#16a34a',
          backgroundColor: '#16a34a',
          borderWidth: 3,
          pointRadius: 4,
          pointHoverRadius: 6,
          tension: 0.35,
          data: base.map((v) => v.margem),
          yAxisID: 'y1',
          order: 1,
        },
      ],
    };
  }, [frotaFiltrada]);

  const opcoesGrafico = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          position: 'top',
          labels: {
            usePointStyle: true,
            boxWidth: 10,
            color: '#27324d',
            font: {
              weight: 700,
            },
          },
        },
        title: {
          display: true,
          text: 'Receita vs Despesa vs Margem — visão comparativa',
          color: '#11247c',
          font: {
            size: 16,
            weight: 800,
          },
          padding: {
            bottom: 16,
          },
        },
        tooltip: {
          backgroundColor: 'rgba(17, 25, 40, 0.94)',
          padding: 12,
          cornerRadius: 12,
          callbacks: {
            label(context) {
              let label = context.dataset.label || '';
              if (label) label += ': ';
              if (context.dataset.type === 'line') {
                return `${label}${Number(context.raw || 0).toFixed(2)}%`;
              }
              return `${label}${formatCurrency(context.raw)}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: '#5a6275',
            maxRotation: 35,
            minRotation: 0,
            font: {
              size: 11,
              weight: 600,
            },
          },
        },
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          grid: {
            color: 'rgba(15, 23, 42, 0.07)',
          },
          ticks: {
            color: '#5a6275',
            callback: (value) => formatCurrency(value),
          },
          title: {
            display: true,
            text: 'Valores (R$)',
            color: '#334155',
            font: {
              weight: 700,
            },
          },
        },
        y1: {
          type: 'linear',
          display: true,
          position: 'right',
          grid: {
            drawOnChartArea: false,
          },
          ticks: {
            color: '#5a6275',
            callback: (value) => `${value}%`,
          },
          title: {
            display: true,
            text: 'Margem (%)',
            color: '#334155',
            font: {
              weight: 700,
            },
          },
        },
      },
    };
  }, []);

  const diagnosticosGerais = useMemo(() => {
    const items = [];

    if (analiseGeral.veiculosPrejuizo > 0) {
      items.push({
        tone: 'danger',
        title: 'Atenção crítica',
        text: `Detectamos ${analiseGeral.veiculosPrejuizo} veículo(s) operando com prejuízo no período atual. O foco deve ser o controle de custo e a revisão de produtividade desses ativos.`,
      });
    } else {
      items.push({
        tone: 'success',
        title: 'Saúde financeira',
        text: 'Nenhum veículo operando no vermelho no período filtrado. O comportamento geral da frota está financeiramente saudável.',
      });
    }

    if (analiseGeral.ociosos > 0) {
      items.push({
        tone: 'warning',
        title: 'Possível ociosidade',
        text: `${analiseGeral.ociosos} veículo(s) sem movimentação financeira identificados. Vale confirmar se houve indisponibilidade, baixa utilização ou ausência de apontamentos.`,
      });
    }

    if (analiseGeral.melhorVeiculo) {
      items.push({
        tone: 'highlight',
        title: 'MVP da frota',
        text: `O veículo ${analiseGeral.melhorVeiculo.modelo} (${analiseGeral.melhorVeiculo.plateLabel}) lidera em resultado líquido com ${formatCurrency(
          analiseGeral.melhorVeiculo.lucro_liquido
        )}.`,
      });
    }

    if (
      analiseGeral.piorVeiculo &&
      analiseGeral.piorVeiculo.lucro_liquido < 0
    ) {
      items.push({
        tone: 'warning',
        title: 'Ofensor de margem',
        text: `O veículo ${analiseGeral.piorVeiculo.modelo} (${analiseGeral.piorVeiculo.plateLabel}) é o principal ofensor de rentabilidade no período, exigindo ação direcionada.`,
      });
    }

    return items.slice(0, 4);
  }, [analiseGeral]);

  const modalVehicle = selectedVehicle;

  return (
    <div className="rfleet-page">
      <section className="rfleet-hero">
        <div className="rfleet-hero__content">
          <div className="rfleet-hero__titleWrap">
            <div className="rfleet-hero__icon">
              <FaChartLine />
            </div>
            <div>
              <h1 className="rfleet-hero__title">Dashboard de Rentabilidade da Frota</h1>
              <p className="rfleet-hero__subtitle">
                Visão executiva, filtros dinâmicos e detalhamento inteligente por veículo.
              </p>
            </div>
          </div>

          <div className="rfleet-toolbar">
            <div className="rfleet-toolbar__main">
              <div className="rfleet-field rfleet-field--period">
                <span className="rfleet-field__icon">
                  <FaFilter />
                </span>
                <select value={periodo} onChange={(e) => setPeriodo(e.target.value)}>
                  <option value="semanal">Últimos 7 dias</option>
                  <option value="mensal">Este mês</option>
                  <option value="trimestral">Último trimestre</option>
                  <option value="anual">Este ano</option>
                </select>
              </div>

              <div className="rfleet-field rfleet-field--search">
                <span className="rfleet-field__icon">
                  <FaSearch />
                </span>
                <input
                  type="text"
                  placeholder="Buscar por modelo ou placa..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                />
              </div>

              <div className="rfleet-field">
                <span className="rfleet-field__icon">
                  <FaBalanceScale />
                </span>
                <select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
                  <option value="todos">Todos os status</option>
                  <option value="rentavel">Somente rentáveis</option>
                  <option value="prejuizo">Somente prejuízo</option>
                </select>
              </div>

              <div className="rfleet-field">
                <span className="rfleet-field__icon">
                  <FaSortAmountDown />
                </span>
                <select value={ordenacao} onChange={(e) => setOrdenacao(e.target.value)}>
                  <option value="lucro_desc">Maior lucro</option>
                  <option value="lucro_asc">Menor lucro</option>
                  <option value="margem_desc">Maior margem</option>
                  <option value="receita_desc">Maior receita</option>
                  <option value="roi_desc">Maior ROI</option>
                  <option value="modelo_asc">Modelo A-Z</option>
                </select>
              </div>
            </div>

            <div className="rfleet-toolbar__chips">
              <button
                type="button"
                className={`rfleet-chip ${quickFilter === 'todos' ? 'is-active' : ''}`}
                onClick={() => setQuickFilter('todos')}
              >
                Todos
              </button>
              <button
                type="button"
                className={`rfleet-chip ${quickFilter === 'prejuizo' ? 'is-active is-danger' : ''}`}
                onClick={() => setQuickFilter('prejuizo')}
              >
                No vermelho
              </button>
              <button
                type="button"
                className={`rfleet-chip ${quickFilter === 'top_margem' ? 'is-active is-success' : ''}`}
                onClick={() => setQuickFilter('top_margem')}
              >
                Top margem
              </button>
              <button
                type="button"
                className={`rfleet-chip ${quickFilter === 'ociosos' ? 'is-active is-neutral' : ''}`}
                onClick={() => setQuickFilter('ociosos')}
              >
                Ociosos
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="rfleet-kpis">
        <article className="rfleet-kpi rfleet-kpi--blue">
          <div className="rfleet-kpi__icon">
            <FaArrowUp />
          </div>
          <div className="rfleet-kpi__content">
            <span>Faturamento total</span>
            <strong>{formatCurrency(analiseGeral.totalReceita)}</strong>
          </div>
        </article>

        <article className="rfleet-kpi rfleet-kpi--red">
          <div className="rfleet-kpi__icon">
            <FaArrowDown />
          </div>
          <div className="rfleet-kpi__content">
            <span>Custo operacional</span>
            <strong>{formatCurrency(analiseGeral.totalDespesa)}</strong>
          </div>
        </article>

        <article className="rfleet-kpi rfleet-kpi--green">
          <div className="rfleet-kpi__icon">
            <FaMoneyBillWave />
          </div>
          <div className="rfleet-kpi__content">
            <span>Lucro líquido</span>
            <strong>{formatCurrency(analiseGeral.lucroTotal)}</strong>
          </div>
        </article>

        <article
          className={`rfleet-kpi ${
            analiseGeral.margemLucro >= 20 ? 'rfleet-kpi--gold' : 'rfleet-kpi--gray'
          }`}
        >
          <div className="rfleet-kpi__icon rfleet-kpi__icon--percent">%</div>
          <div className="rfleet-kpi__content">
            <span>Margem da frota</span>
            <strong>{formatPercent(analiseGeral.margemLucro)}</strong>
          </div>
        </article>

        <article className="rfleet-kpi rfleet-kpi--indigo">
          <div className="rfleet-kpi__icon">
            <FaCoins />
          </div>
          <div className="rfleet-kpi__content">
            <span>Score médio da frota</span>
            <strong>{analiseGeral.scoreMedio.toFixed(0)}/100</strong>
          </div>
        </article>
      </section>

      <section className="rfleet-mainGrid">
        <div className="rfleet-card rfleet-card--chart">
          <div className="rfleet-card__header">
            <div>
              <h3>Comparativo financeiro</h3>
              <p>
                Exibindo até 12 veículos com maior receita dentro do filtro aplicado.
              </p>
            </div>
            <div className="rfleet-badge">
              {frotaFiltrada.length} veículo(s) filtrado(s)
            </div>
          </div>

          <div className="rfleet-chartWrap">
            <Chart type="bar" data={dadosGrafico} options={opcoesGrafico} />
          </div>
        </div>

        <aside className="rfleet-card rfleet-card--insights">
          <div className="rfleet-card__header">
            <div>
              <h3>
                <FaExclamationTriangle />
                Diagnóstico inteligente
              </h3>
              <p>Leitura executiva consolidada do período.</p>
            </div>
          </div>

          <div className="rfleet-insightList">
            {diagnosticosGerais.map((item, idx) => (
              <div key={`${item.title}-${idx}`} className={`rfleet-insight rfleet-insight--${item.tone}`}>
                <strong>{item.title}</strong>
                <p>{item.text}</p>
              </div>
            ))}
          </div>
        </aside>
      </section>

      <section className="rfleet-card rfleet-card--garage">
        <div className="rfleet-card__header">
          <div>
            <h3>
              <FaCarSide />
              Garagem analítica
            </h3>
            <p>
              Clique em um veículo para abrir o consolidado executivo detalhado.
            </p>
          </div>

          <div className="rfleet-card__headerMeta">
            {loading ? 'Atualizando...' : `${frotaFiltrada.length} resultado(s)`}
          </div>
        </div>

        {loading ? (
          <div className="rfleet-emptyState">
            <p>Calculando métricas e preparando a visão analítica da frota...</p>
          </div>
        ) : frotaFiltrada.length === 0 ? (
          <div className="rfleet-emptyState">
            <p>Nenhum veículo encontrado com os filtros aplicados.</p>
          </div>
        ) : (
          <div className="rfleet-vehicleGrid">
            {frotaFiltrada.map((vehicle) => (
  <button
    key={vehicle.id}
    type="button"
    className={`rfleet-vehicleCard rfleet-vehicleCard--${vehicle.statusKey}`}
    onClick={() => setSelectedVehicle(vehicle)}
  >
    <div className="rfleet-vehicleCard__hero">
      <div className="rfleet-vehicleCard__heroLeft">
        <div className="rfleet-plate rfleet-plate--compact">
          <div className="rfleet-plate__header">
            <span className="rfleet-plate__country">BR</span>
            <span className="rfleet-plate__mercosul">MERCOSUL • BRASIL</span>
          </div>
          <div className="rfleet-plate__body">{vehicle.plateLabel}</div>
        </div>

        <div className="rfleet-vehicleCard__identity">
          <h4>{vehicle.modelo || 'Veículo sem identificação'}</h4>
          <p>Consolidado de performance financeira do período selecionado</p>
        </div>
      </div>

      <div className="rfleet-vehicleCard__heroRight">
        <div className={`rfleet-score rfleet-score--${vehicle.scoreTone} rfleet-score--compact`}>
          <span>Score</span>
          <strong>{vehicle.performanceScore}</strong>
        </div>

        <span className={`rfleet-status rfleet-status--${vehicle.statusKey}`}>
          {vehicle.statusLabel}
        </span>
      </div>
    </div>

    <div className="rfleet-vehicleCard__statsGrid">
      <div className="rfleet-vehicleStat">
        <span>Receita</span>
        <strong className="is-blue">{formatCurrency(vehicle.total_receita)}</strong>
      </div>

      <div className="rfleet-vehicleStat">
        <span>Despesa</span>
        <strong className="is-red">{formatCurrency(vehicle.total_despesa)}</strong>
      </div>

      <div className="rfleet-vehicleStat">
        <span>Lucro líquido</span>
        <strong className={vehicle.lucro_liquido >= 0 ? 'is-green' : 'is-red'}>
          {formatCurrency(vehicle.lucro_liquido)}
        </strong>
      </div>

      <div className="rfleet-vehicleStat">
        <span>ROI</span>
        <strong>{formatPercent(vehicle.roi)}</strong>
      </div>
    </div>

    <div className="rfleet-vehicleCard__performance">
      <div className="rfleet-vehicleCard__performanceHead">
        <span>Margem operacional</span>
        <strong>{formatPercent(vehicle.margem)}</strong>
      </div>

      <div className="rfleet-performanceBar__track rfleet-performanceBar__track--card">
        <div
          className={`rfleet-performanceBar__fill ${
            vehicle.margem >= 0 ? 'is-positive' : 'is-negative'
          }`}
          style={{ width: `${Math.min(Math.abs(vehicle.margem), 100)}%` }}
        />
      </div>
    </div>

    <div className="rfleet-vehicleCard__footer">
      <div className="rfleet-vehicleCard__footerInfo">
        <span>Eficiência do ativo</span>
        <strong>{vehicle.performanceScore >= 75 ? 'Alto desempenho' : vehicle.performanceScore >= 50 ? 'Boa estabilidade' : vehicle.performanceScore >= 35 ? 'Ponto de atenção' : 'Crítico'}</strong>
      </div>

      <span className="rfleet-linkish rfleet-linkish--button">
        Abrir consolidado
      </span>
    </div>
  </button>
))}
          </div>
        )}
      </section>

      <section className="rfleet-card rfleet-card--table">
        <div className="rfleet-card__header">
          <div>
            <h3>Detalhamento por veículo</h3>
            <p>Base tabular para leitura operacional e comparação rápida.</p>
          </div>
        </div>

        <div className="rfleet-tableWrap">
          <table className="rfleet-table">
            <thead>
              <tr>
                <th>Veículo / Placa</th>
                <th>Receita</th>
                <th>Despesa</th>
                <th>Lucro R$</th>
                <th>Margem</th>
                <th>ROI</th>
                <th>Score</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="rfleet-table__empty">
                    Calculando métricas...
                  </td>
                </tr>
              ) : frotaFiltrada.length === 0 ? (
                <tr>
                  <td colSpan="8" className="rfleet-table__empty">
                    Nenhum registro encontrado para os filtros aplicados.
                  </td>
                </tr>
              ) : (
                frotaFiltrada.map((v) => (
                  <tr
                    key={`table-${v.id}`}
                    className={v.lucro_liquido < 0 ? 'is-loss' : ''}
                    onClick={() => setSelectedVehicle(v)}
                  >
                    <td>
                      <div className="rfleet-tableVehicle">
                        <span className="rfleet-tableVehicle__model">{v.modelo}</span>
                        <span className="rfleet-tableVehicle__plate">{v.plateLabel}</span>
                      </div>
                    </td>
                    <td className="is-blue">{formatCurrency(v.total_receita)}</td>
                    <td className="is-red">{formatCurrency(v.total_despesa)}</td>
                    <td className={v.lucro_liquido >= 0 ? 'is-green is-strong' : 'is-red is-strong'}>
                      {formatCurrency(v.lucro_liquido)}
                    </td>
                    <td>
                      <div className="rfleet-miniBar">
                        <div
                          className={`rfleet-miniBar__fill ${v.margem >= 0 ? 'is-positive' : 'is-negative'}`}
                          style={{ width: `${Math.min(Math.abs(v.margem), 100)}%` }}
                        />
                      </div>
                      <span className="rfleet-miniBar__label">{formatPercent(v.margem)}</span>
                    </td>
                    <td>{formatPercent(v.roi)}</td>
                    <td>
                      <span className={`rfleet-inlineScore rfleet-inlineScore--${v.scoreTone}`}>
                        {v.performanceScore}
                      </span>
                    </td>
                    <td>
                      <span className={`rfleet-status rfleet-status--${v.statusKey}`}>
                        {v.statusLabel}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {modalVehicle && (
        <div className="rfleet-modalOverlay" onClick={() => setSelectedVehicle(null)}>
          <div className="rfleet-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rfleet-modal__header">
              <div className="rfleet-modal__headerLeft">
                <div className="rfleet-plate rfleet-plate--modal">
                  <div className="rfleet-plate__header">
                    <span className="rfleet-plate__country">BR</span>
                    <span className="rfleet-plate__mercosul">MERCOSUL • BRASIL</span>
                  </div>
                  <div className="rfleet-plate__body">{modalVehicle.plateLabel}</div>
                </div>

                <div className="rfleet-modal__vehicleInfo">
                  <h3>{modalVehicle.modelo || 'Veículo sem identificação'}</h3>
                  <p>Consolidado financeiro e leitura executiva do ativo no período selecionado.</p>
                  <div className="rfleet-modal__badges">
                    <span className={`rfleet-status rfleet-status--${modalVehicle.statusKey}`}>
                      {modalVehicle.statusLabel}
                    </span>
                    <span className={`rfleet-inlineScore rfleet-inlineScore--${modalVehicle.scoreTone}`}>
                      Score {modalVehicle.performanceScore}
                    </span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="rfleet-modal__close"
                onClick={() => setSelectedVehicle(null)}
                aria-label="Fechar modal"
              >
                <FaTimes />
              </button>
            </div>

            <div className="rfleet-modal__body">
              <div className="rfleet-modal__summaryCard">
                <div className="rfleet-modal__summaryIcon">
                  <FaInfoCircle />
                </div>
                <div>
                  <h4>Resumo executivo</h4>
                  <p>{modalVehicle.executiveSummary}</p>
                </div>
              </div>

              <div className="rfleet-modal__metrics">
                <div className="rfleet-modalMetric">
                  <span>Receita total</span>
                  <strong className="is-blue">{formatCurrency(modalVehicle.total_receita)}</strong>
                </div>
                <div className="rfleet-modalMetric">
                  <span>Despesa total</span>
                  <strong className="is-red">{formatCurrency(modalVehicle.total_despesa)}</strong>
                </div>
                <div className="rfleet-modalMetric">
                  <span>Lucro líquido</span>
                  <strong className={modalVehicle.lucro_liquido >= 0 ? 'is-green' : 'is-red'}>
                    {formatCurrency(modalVehicle.lucro_liquido)}
                  </strong>
                </div>
                <div className="rfleet-modalMetric">
                  <span>Margem</span>
                  <strong>{formatPercent(modalVehicle.margem)}</strong>
                </div>
                <div className="rfleet-modalMetric">
                  <span>ROI</span>
                  <strong>{formatPercent(modalVehicle.roi)}</strong>
                </div>
                <div className="rfleet-modalMetric">
                  <span>Eficiência / score</span>
                  <strong>{modalVehicle.performanceScore}/100</strong>
                </div>
              </div>

              <div className="rfleet-modal__grid">
                <div className="rfleet-modalPanel">
                  <h4>Indicadores consolidados</h4>

                  <div className="rfleet-modalIndicator">
                    <div className="rfleet-modalIndicator__label">
                      <span>Margem operacional</span>
                      <strong>{formatPercent(modalVehicle.margem)}</strong>
                    </div>
                    <div className="rfleet-performanceBar__track is-tall">
                      <div
                        className={`rfleet-performanceBar__fill ${
                          modalVehicle.margem >= 0 ? 'is-positive' : 'is-negative'
                        }`}
                        style={{ width: `${Math.min(Math.abs(modalVehicle.margem), 100)}%` }}
                      />
                    </div>
                  </div>

                  <div className="rfleet-modalIndicator">
                    <div className="rfleet-modalIndicator__label">
                      <span>Custo sobre receita</span>
                      <strong>{formatPercent(modalVehicle.custoSobreReceita)}</strong>
                    </div>
                    <div className="rfleet-performanceBar__track is-tall">
                      <div
                        className={`rfleet-performanceBar__fill ${
                          modalVehicle.custoSobreReceita <= 70 ? 'is-positive' : 'is-negative'
                        }`}
                        style={{ width: `${Math.min(Math.abs(modalVehicle.custoSobreReceita), 100)}%` }}
                      />
                    </div>
                  </div>

                  <div className="rfleet-modalIndicator">
                    <div className="rfleet-modalIndicator__label">
                      <span>ROI</span>
                      <strong>{formatPercent(modalVehicle.roi)}</strong>
                    </div>
                    <div className="rfleet-performanceBar__track is-tall">
                      <div
                        className="rfleet-performanceBar__fill is-positive"
                        style={{ width: `${Math.min(Math.abs(modalVehicle.roi), 100)}%` }}
                      />
                    </div>
                  </div>
                </div>

                <div className="rfleet-modalPanel">
                  <h4>Leitura analítica</h4>

                  <div className="rfleet-modalInsightList">
                    {modalVehicle.insights.map((insight, idx) => (
                      <div
                        key={`${insight.title}-${idx}`}
                        className={`rfleet-modalInsight rfleet-modalInsight--${insight.type}`}
                      >
                        <strong>{insight.title}</strong>
                        <p>{insight.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="rfleet-modal__footer">
              <button
                type="button"
                className="rfleet-modal__action"
                onClick={() => setSelectedVehicle(null)}
              >
                Fechar consolidado
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}